import React, { useState, useRef, useCallback, useId, useMemo } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { useTranslation } from 'localization';
import type { FilterExpression, FilterOperator, PerspectiveFilter } from 'types/filters';
import FilterEditorPanelMolecule from '../../molecules/FilterEditorPanel/FilterEditorPanelMolecule';
import type { PropertyOption, PropertyType } from '../../molecules/PropertyFilter/PropertyFilterMolecule';
import { getFilterOperatorOptions, isUsableBetweenBound, VALUELESS_OPERATORS } from '../../../utils/filterOperators';
import './ColumnFilterHeaderAtom.scss';

export interface ColumnFilterHeaderAtomProps {
  label: string;
  filterType: 'text' | 'enum' | 'date' | 'numeric' | 'perspective';
  enumValues?: Array<{ label: string; value: string }>;
  activeFilter?: FilterExpression;
  activePerspectiveFilter?: PerspectiveFilter;
  onFilterChange: (expression: FilterExpression | null) => void;
  onPerspectiveFilterChange?: (assignees: string[]) => void;
  perspectiveOptions?: Array<{ label: string; value: string }>;
  placeholder?: string;
  /** Called when this filter opens; receives a close callback so the parent can immediately close the previous panel */
  onFilterOpen?: (closeMe: () => void) => void;
}

/**
 * Stable synthesized-catalog key. This column filter always edits exactly one
 * property (the column itself, described by `label`/`filterType`/`enumValues`),
 * so `FilterEditorPanelMolecule`'s picker catalog is a single-entry array keyed
 * on this constant rather than a real schema property key — the caller
 * (`DataListMolecule`) already knows which column field the expression belongs
 * to and re-keys it (`onColumnFilterChange(col.field, expression)`), so the
 * `property` value on the wire is discarded downstream.
 */
const COLUMN_FILTER_PROPERTY_KEY = 'columnValue';

/** Maps this atom's narrower `filterType` union onto the canonical `PropertyType`. */
function toPropertyType(filterType: ColumnFilterHeaderAtomProps['filterType']): PropertyType {
  switch (filterType) {
    case 'numeric':
      return 'numeric';
    case 'date':
      return 'date';
    case 'enum':
      return 'enum';
    case 'text':
    default:
      return 'text';
  }
}

/**
 * Seeds the FilterEditorPanelMolecule-native draft from the persisted
 * `FilterExpression`. `between` needs no bridging: the editor and the wire
 * format (shared with `useColumnFilters`' client-side matcher) both carry the
 * canonical `min`/`max` — a number for a numeric property, an ISO 8601 UTC
 * string for a date (Phase 7a, EAINT-931; never epoch ms).
 */
function seedDraft(
  active: FilterExpression | undefined,
  propertyType: PropertyType,
  t: (key: string) => string,
): FilterExpression {
  if (!active) {
    const ops = getFilterOperatorOptions(propertyType, t);
    return {
      property: COLUMN_FILTER_PROPERTY_KEY,
      operator: ops[0]?.value ?? 'equals',
      value: '',
    };
  }
  return { ...active, property: COLUMN_FILTER_PROPERTY_KEY };
}

/**
 * Converts the FilterEditorPanelMolecule-native draft back to the persisted
 * wire format on Apply, or `null` when the draft carries no effective value
 * (uniform "empty ⇒ no filter" rule, replacing the old per-type-inconsistent
 * emptiness handling — enum used to special-case "all selected", text/numeric
 * never checked at all).
 */
function draftToWire(draft: FilterExpression, propertyType: PropertyType): FilterExpression | null {
  if (VALUELESS_OPERATORS.includes(draft.operator as FilterOperator)) {
    return { property: '', operator: draft.operator };
  }

  if (draft.operator === 'between') {
    const { min, max } = draft;
    if (!isUsableBetweenBound(min) || !isUsableBetweenBound(max)) return null;
    return { property: '', operator: 'between', min, max };
  }

  if (draft.operator === 'in' || draft.operator === 'not_in') {
    // FilterEditorPanelMolecule's MultiSelectAtom writes multi-value
    // selections to `value` (its one uniform field — see `changeValue`), but
    // the persisted wire contract (types/filters.d.ts, and the
    // `useColumnFilters` matcher this atom's only consumer relies on) keys
    // in/not_in off `values` (plural). Bridge the two here.
    let values: unknown[] = [];
    if (Array.isArray(draft.value)) values = draft.value;
    else if (Array.isArray(draft.values)) values = draft.values;
    if (values.length === 0) return null;
    return { property: '', operator: draft.operator, values };
  }

  if (draft.value === undefined || draft.value === null || draft.value === '') {
    return null;
  }
  return { property: '', operator: draft.operator, value: draft.value };
}

const ColumnFilterHeaderAtom: React.FC<ColumnFilterHeaderAtomProps> = ({
  label,
  filterType,
  enumValues,
  activeFilter,
  activePerspectiveFilter,
  onFilterChange,
  onPerspectiveFilterChange,
  perspectiveOptions,
  onFilterOpen,
}) => {
  const { t } = useTranslation();
  const overlayRef = useRef<OverlayPanel>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const uniqueId = useId();

  const propertyType = useMemo(() => toPropertyType(filterType), [filterType]);

  const availableProperties: PropertyOption[] = useMemo(
    () => [
      {
        key: COLUMN_FILTER_PROPERTY_KEY,
        label,
        type: propertyType,
        enumValues,
      },
    ],
    [label, propertyType, enumValues],
  );

  const [draftFilter, setDraftFilter] = useState<FilterExpression>(() => seedDraft(activeFilter, propertyType, t));

  // Perspective draft state — kept bespoke: this panel is handed a flat,
  // already-resolved `perspectiveOptions` list (no sphereId/organizationId/
  // schemaSystemName), so it cannot be swapped for `PerspectiveSearch` /
  // `PerspectiveFilterMolecule` (both fetch their own options and expect a
  // different prop contract) without changing this atom's public API.
  const [draftAssignees, setDraftAssignees] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const isActive =
    filterType === 'perspective'
      ? (activePerspectiveFilter?.assignees?.length ?? 0) > 0
      : activeFilter != null;

  const initDraft = useCallback(() => {
    if (filterType === 'perspective') {
      setDraftAssignees(new Set(activePerspectiveFilter?.assignees ?? []));
      setSearch('');
      return;
    }
    setDraftFilter(seedDraft(activeFilter, propertyType, t));
  }, [filterType, activeFilter, activePerspectiveFilter, propertyType, t]);

  const handleApply = useCallback(() => {
    if (filterType === 'perspective') {
      onPerspectiveFilterChange?.(Array.from(draftAssignees));
    } else {
      onFilterChange(draftToWire(draftFilter, propertyType));
    }
    overlayRef.current?.hide();
  }, [filterType, draftFilter, propertyType, draftAssignees, onFilterChange, onPerspectiveFilterChange]);

  const handleClear = useCallback(() => {
    if (filterType === 'perspective') {
      onPerspectiveFilterChange?.([]);
    } else {
      onFilterChange(null);
    }
    overlayRef.current?.hide();
  }, [filterType, onFilterChange, onPerspectiveFilterChange]);

  const handleFilterClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      initDraft();
      onFilterOpen?.(() => overlayRef.current?.hide());
      overlayRef.current?.toggle(e);
    },
    [initDraft, onFilterOpen],
  );

  const handlePanelShow = useCallback(() => {
    setIsPanelOpen(true);
    requestAnimationFrame(() => {
      const focusTarget = panelRef.current?.querySelector<HTMLElement>(
        'input:not([type="hidden"]), .p-dropdown-trigger, select, button',
      );
      focusTarget?.focus();
    });
  }, []);

  const handlePanelHide = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  const handlePanelKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      overlayRef.current?.hide();
    }
  }, []);

  const renderPerspectivePanel = () => {
    const opts = perspectiveOptions || [];
    const filtered = search
      ? opts.filter((o) =>
          o.label.toLowerCase().includes(search.toLowerCase()),
        )
      : opts;

    return (
      <>
        <InputText
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('columnFilters.searchSpheres')}
          className="column-filter-header__input"
          onKeyDown={(e) => e.stopPropagation()}
          aria-label={t('columnFilters.searchSpheres')}
        />
        <div className="column-filter-header__enum-list" role="group" aria-label={label}>
          {filtered.map((item) => {
            const itemId = `${uniqueId}-perspective-${item.value}`;
            return (
              <div key={item.value} className="column-filter-header__enum-row">
                <Checkbox
                  inputId={itemId}
                  checked={draftAssignees.has(item.value)}
                  onChange={() => {
                    setDraftAssignees((prev) => {
                      const next = new Set(prev);
                      if (next.has(item.value)) next.delete(item.value);
                      else next.add(item.value);
                      return next;
                    });
                  }}
                />
                <label htmlFor={itemId} className="column-filter-header__enum-label">
                  {item.label}
                </label>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="column-filter-header__no-results">
              {t('columnFilters.noSpheresFound')}
            </div>
          )}
        </div>
      </>
    );
  };

  const triggerAriaLabel = isActive
    ? `${label} – ${t('columnFilters.filterActive')}`
    : `${label} ${t('filters.filter')}`;

  return (
    <div className="column-filter-header">
      <span className="column-filter-header__label">{label}</span>
      <button
        className={`column-filter-header__trigger ${isActive ? 'column-filter-header__trigger--active' : ''}`}
        onClick={handleFilterClick}
        aria-label={triggerAriaLabel}
        aria-haspopup="dialog"
        aria-expanded={isPanelOpen}
        type="button"
      >
        <i
          className={`pi ${isActive ? 'pi-filter-fill' : 'pi-filter-slash'}`}
          aria-hidden="true"
        />
      </button>

      <span
        className="column-filter-header__sr-only"
        aria-live="polite"
        role="status"
      >
        {isActive ? `${label}: ${t('columnFilters.filterActive')}` : ''}
      </span>

      <OverlayPanel
        ref={overlayRef}
        className="column-filter-header-overlay"
        dismissable
        onShow={handlePanelShow}
        onHide={handlePanelHide}
      >
        <div
          className="column-filter-header__content"
          ref={panelRef}
          onKeyDown={handlePanelKeyDown}
          role="dialog"
          aria-label={`${label} ${t('filters.filter')}`}
        >
          <div className="column-filter-header__panel">
            {filterType === 'perspective' ? (
              renderPerspectivePanel()
            ) : (
              <FilterEditorPanelMolecule
                filter={draftFilter}
                availableProperties={availableProperties}
                onChange={setDraftFilter}
              />
            )}
          </div>
          <div className="column-filter-header__actions">
            <Button
              label={t('common.actions.apply')}
              className="p-button-sm Save-button"
              onClick={handleApply}
            />
            <Button
              label={t('filters.clearAll')}
              className="p-button-text p-button-sm Close-button"
              onClick={handleClear}
            />
          </div>
        </div>
      </OverlayPanel>
    </div>
  );
};

export default ColumnFilterHeaderAtom;
